/**
 * Super Admin - User Profile View/Edit Page
 * Allows Super Admin to view and edit staff member information
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import { superAdminAPI } from "utils/api";

function UserProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [editMode, setEditMode] = useState(false);
  const [profileData, setProfileData] = useState({
    fullName: "",
    email: "",
    idNumber: "",
    phone: "",
    companyNumber: "",
    relevantPhoneNumbers: ["", ""],
    address: "",
    maritalStatus: "Single",
    familyMembersNumber: 0,
    personalEmail: "",
    nationality: "",
    gender: "Male",
    role: null,
    photo: null,
    idPhoto: null,
    createdAt: null,
    isActive: true,
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [idPhotoFile, setIdPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [idPhotoPreview, setIdPhotoPreview] = useState(null);

  useEffect(() => {
    fetchUserProfile();
  }, [id]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const response = await superAdminAPI.getUserById(id);
      if (response.user) {
        const user = response.user;
        // Parse relevantPhoneNumbers - handle both array and string formats
        let relevantPhones = ["", ""];
        if (user.relevantPhoneNumbers) {
          if (Array.isArray(user.relevantPhoneNumbers)) {
            // Already an array, map to strings and trim
            relevantPhones = user.relevantPhoneNumbers.map((phone) => String(phone || "").trim());
          } else if (typeof user.relevantPhoneNumbers === "string") {
            // Handle malformed JSON strings like '["01117766693"' or '["01117766693"]'
            let str = user.relevantPhoneNumbers.trim();

            // Remove brackets if present
            str = str.replace(/^\[|\]$/g, "");

            // Extract phone numbers from the string
            // Match quoted strings or plain numbers
            const matches = str.match(/"([^"]+)"|'([^']+)'|(\d+)/g);
            if (matches) {
              relevantPhones = matches
                .map((match) => {
                  // Remove quotes
                  return match.replace(/^["']|["']$/g, "").trim();
                })
                .filter((phone) => phone);
            } else {
              // If no matches, try to extract any number sequence
              const numberMatch = str.match(/\d+/);
              if (numberMatch) {
                relevantPhones = [numberMatch[0]];
              }
            }
          }
          // Ensure we have exactly 2 elements
          while (relevantPhones.length < 2) {
            relevantPhones.push("");
          }
          relevantPhones = relevantPhones.slice(0, 2);
        }

        setProfileData({
          fullName: user.fullName || "",
          email: user.email || "",
          idNumber: user.idNumber || "",
          phone: user.phone || "",
          companyNumber: user.companyNumber || "",
          relevantPhoneNumbers: relevantPhones,
          address: user.address || "",
          maritalStatus: user.maritalStatus || "Single",
          familyMembersNumber: user.familyMembersNumber || 0,
          personalEmail: user.personalEmail || "",
          nationality: user.nationality || "",
          gender: user.gender || "Male",
          role: user.role,
          photo: user.photo,
          idPhoto: user.idPhoto,
          createdAt: user.createdAt,
          isActive: user.isActive !== false,
        });
        setPhotoPreview(user.photo?.url);
        setIdPhotoPreview(user.idPhoto?.url);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setMessage({ type: "error", text: "Failed to load user profile. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("relevantPhone")) {
      const index = parseInt(name.replace("relevantPhone", ""));
      const newPhones = [...profileData.relevantPhoneNumbers];
      newPhones[index] = value;
      setProfileData({ ...profileData, relevantPhoneNumbers: newPhones });
    } else {
      setProfileData({ ...profileData, [name]: value });
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: "error", text: "Photo size must be less than 5MB" });
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleIdPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setMessage({ type: "error", text: "ID Photo size must be less than 10MB" });
        return;
      }
      setIdPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setIdPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      const formData = new FormData();
      formData.append("fullName", profileData.fullName);
      formData.append("phone", profileData.phone);
      // Send relevant phone numbers as clean array (filter out empty values)
      const cleanPhones = profileData.relevantPhoneNumbers
        .map((p) => String(p || "").trim())
        .filter((p) => p !== "");
      if (cleanPhones.length > 0) {
        formData.append("relevantPhoneNumbers", JSON.stringify(cleanPhones));
      }
      formData.append("address", profileData.address);
      formData.append("maritalStatus", profileData.maritalStatus);
      formData.append("familyMembersNumber", profileData.familyMembersNumber);
      formData.append("personalEmail", profileData.personalEmail);
      formData.append("nationality", profileData.nationality);
      formData.append("gender", profileData.gender);

      if (photoFile) {
        formData.append("photo", photoFile);
      }
      if (idPhotoFile) {
        formData.append("idPhoto", idPhotoFile);
      }

      const response = await superAdminAPI.updateUserAccount(id, formData);
      if (response.success) {
        setMessage({ type: "success", text: "User profile updated successfully." });
        setEditMode(false);
        await fetchUserProfile();
      }
    } catch (error) {
      console.error("Error updating user profile:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to update user profile. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Header */}
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDBox>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/super-admin/users")}
              startIcon={<Icon>arrow_back</Icon>}
              sx={{ mb: 2 }}
            >
              Back to Users
            </MDButton>
            <MDTypography variant="h4" fontWeight="bold">
              User Profile
            </MDTypography>
            <MDTypography variant="body2" color="text">
              View and manage staff member information
            </MDTypography>
          </MDBox>
          <MDBox>
            {!editMode ? (
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => setEditMode(true)}
                startIcon={<Icon>edit</Icon>}
              >
                Edit Profile
              </MDButton>
            ) : (
              <MDBox display="flex" gap={1}>
                <MDButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    setEditMode(false);
                    fetchUserProfile();
                  }}
                  disabled={submitting}
                >
                  Cancel
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="success"
                  onClick={handleSubmit}
                  disabled={submitting}
                  startIcon={<Icon>save</Icon>}
                >
                  {submitting ? "Saving..." : "Save Changes"}
                </MDButton>
              </MDBox>
            )}
          </MDBox>
        </MDBox>

        {/* Message Alert */}
        {message.text && (
          <MDBox mb={3}>
            <Alert severity={message.type} onClose={() => setMessage({ type: "", text: "" })}>
              {message.text}
            </Alert>
          </MDBox>
        )}

        <Grid container spacing={3}>
          {/* Left Column - Photo and Documents */}
          <Grid item xs={12} md={4}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Profile Photo
                </MDTypography>
                <MDBox display="flex" flexDirection="column" alignItems="center" mb={3}>
                  {photoPreview ? (
                    <Avatar
                      src={photoPreview}
                      alt={profileData.fullName}
                      sx={{ width: 150, height: 150, mb: 2 }}
                    />
                  ) : (
                    <Avatar sx={{ width: 150, height: 150, mb: 2 }}>
                      <Icon sx={{ fontSize: "4rem" }}>person</Icon>
                    </Avatar>
                  )}
                  {editMode && (
                    <Button
                      variant="contained"
                      component="label"
                      size="small"
                      startIcon={<Icon>photo_camera</Icon>}
                      disabled={submitting}
                      sx={{
                        backgroundColor: "info.main",
                        color: "white !important",
                        "& .MuiSvgIcon-root": {
                          color: "white !important",
                        },
                        "&:hover": {
                          backgroundColor: "info.dark",
                          color: "white !important",
                        },
                      }}
                    >
                      Change Photo
                      <input type="file" hidden accept="image/*" onChange={handlePhotoChange} />
                    </Button>
                  )}
                </MDBox>

                <Divider sx={{ my: 3 }} />

                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  ID Photo
                </MDTypography>
                {idPhotoPreview ? (
                  <MDBox textAlign="center">
                    <Chip
                      icon={<Icon>description</Icon>}
                      label="ID Photo Uploaded"
                      color="success"
                      size="medium"
                      sx={{ mb: 1 }}
                    />
                    {editMode && (
                      <Button
                        variant="contained"
                        component="label"
                        size="small"
                        startIcon={<Icon>upload_file</Icon>}
                        disabled={submitting}
                        fullWidth
                        sx={{
                          backgroundColor: "info.main",
                          color: "white !important",
                          "& .MuiSvgIcon-root": {
                            color: "white !important",
                          },
                          "&:hover": {
                            backgroundColor: "info.dark",
                            color: "white !important",
                          },
                        }}
                      >
                        Change ID Photo
                        <input
                          type="file"
                          hidden
                          accept="application/pdf"
                          onChange={handleIdPhotoChange}
                        />
                      </Button>
                    )}
                  </MDBox>
                ) : (
                  <MDBox textAlign="center">
                    <Chip
                      icon={<Icon>description</Icon>}
                      label="No ID Photo"
                      color="error"
                      size="medium"
                      sx={{ mb: 1 }}
                    />
                    {editMode && (
                      <Button
                        variant="contained"
                        component="label"
                        size="small"
                        startIcon={<Icon>upload_file</Icon>}
                        disabled={submitting}
                        fullWidth
                        sx={{
                          backgroundColor: "info.main",
                          color: "white !important",
                          "& .MuiSvgIcon-root": {
                            color: "white !important",
                          },
                          "&:hover": {
                            backgroundColor: "info.dark",
                            color: "white !important",
                          },
                        }}
                      >
                        Upload ID Photo
                        <input
                          type="file"
                          hidden
                          accept="application/pdf"
                          onChange={handleIdPhotoChange}
                        />
                      </Button>
                    )}
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* Right Column - User Information */}
          <Grid item xs={12} md={8}>
            <Card>
              <MDBox p={3}>
                {/* Basic Information */}
                <MDBox mb={4}>
                  <MDTypography
                    variant="h6"
                    fontWeight="medium"
                    mb={2}
                    display="flex"
                    alignItems="center"
                  >
                    <Icon sx={{ mr: 1, color: "info.main" }}>person</Icon>
                    Basic Information
                  </MDTypography>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        label="Full Name"
                        name="fullName"
                        value={profileData.fullName}
                        onChange={handleChange}
                        disabled={!editMode || submitting}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon color="action">badge</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        label="Email"
                        name="email"
                        value={profileData.email}
                        disabled
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon color="action">email</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        label="ID Number"
                        name="idNumber"
                        value={profileData.idNumber}
                        disabled
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon color="action">credit_card</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        label="Gender"
                        name="gender"
                        value={profileData.gender}
                        onChange={handleChange}
                        disabled={!editMode || submitting}
                        select
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "40px",
                          },
                          "& .MuiSelect-select": {
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            paddingTop: "16px",
                            paddingBottom: "16px",
                          },
                        }}
                      >
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                      </MDInput>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        label="Personal Email"
                        name="personalEmail"
                        value={profileData.personalEmail}
                        onChange={handleChange}
                        disabled={!editMode || submitting}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon color="action">email</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        label="Nationality"
                        name="nationality"
                        value={profileData.nationality}
                        onChange={handleChange}
                        disabled={!editMode || submitting}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon color="action">public</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </MDBox>

                {/* Contact Information */}
                <MDBox mb={4}>
                  <MDTypography
                    variant="h6"
                    fontWeight="medium"
                    mb={2}
                    display="flex"
                    alignItems="center"
                  >
                    <Icon sx={{ mr: 1, color: "info.main" }}>phone</Icon>
                    Contact Information
                  </MDTypography>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={profileData.phone}
                        onChange={handleChange}
                        disabled={!editMode || submitting}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon color="action">phone</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        label="Company Number"
                        name="companyNumber"
                        value={profileData.companyNumber}
                        disabled
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon color="action">business</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        label="Relevant Phone Number 1"
                        name="relevantPhone0"
                        value={profileData.relevantPhoneNumbers[0] || ""}
                        onChange={handleChange}
                        disabled={!editMode || submitting}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon color="action">phone_android</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        label="Relevant Phone Number 2"
                        name="relevantPhone1"
                        value={profileData.relevantPhoneNumbers[1] || ""}
                        onChange={handleChange}
                        disabled={!editMode || submitting}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon color="action">phone_android</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <MDInput
                        fullWidth
                        label="Address"
                        name="address"
                        value={profileData.address}
                        onChange={handleChange}
                        disabled={!editMode || submitting}
                        multiline
                        rows={3}
                      />
                    </Grid>
                  </Grid>
                </MDBox>

                {/* Personal Information */}
                <MDBox mb={4}>
                  <MDTypography
                    variant="h6"
                    fontWeight="medium"
                    mb={2}
                    display="flex"
                    alignItems="center"
                  >
                    <Icon sx={{ mr: 1, color: "info.main" }}>family_restroom</Icon>
                    Personal Information
                  </MDTypography>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        label="Marital Status"
                        name="maritalStatus"
                        value={profileData.maritalStatus}
                        onChange={handleChange}
                        disabled={!editMode || submitting}
                        select
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "40px",
                          },
                          "& .MuiSelect-select": {
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            paddingTop: "5px",
                            paddingBottom: "5px",
                          },
                        }}
                      >
                        <MenuItem value="Single">Single</MenuItem>
                        <MenuItem value="Married">Married</MenuItem>
                        <MenuItem value="Divorced">Divorced</MenuItem>
                        <MenuItem value="Widowed">Widowed</MenuItem>
                      </MDInput>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        label="Family Members Number"
                        name="familyMembersNumber"
                        type="number"
                        value={profileData.familyMembersNumber}
                        onChange={handleChange}
                        disabled={!editMode || submitting}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon color="action">people</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </MDBox>

                {/* Account Information */}
                <MDBox mb={4}>
                  <MDTypography
                    variant="h6"
                    fontWeight="medium"
                    mb={2}
                    display="flex"
                    alignItems="center"
                  >
                    <Icon sx={{ mr: 1, color: "info.main" }}>account_circle</Icon>
                    Account Information
                  </MDTypography>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        fullWidth
                        label="Role"
                        value={profileData.role?.name || "N/A"}
                        disabled
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon color="action">badge</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox>
                        <MDTypography variant="caption" color="text" fontWeight="medium" mb={1}>
                          Status
                        </MDTypography>
                        <MDBox mt={0.5}>
                          <Chip
                            label={profileData.isActive ? "Active" : "Inactive"}
                            color={profileData.isActive ? "success" : "error"}
                            icon={<Icon>{profileData.isActive ? "check_circle" : "block"}</Icon>}
                          />
                        </MDBox>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox>
                        <MDTypography variant="caption" color="text" fontWeight="medium" mb={1}>
                          Joined Date
                        </MDTypography>
                        <MDTypography variant="body2" color="text">
                          {formatDate(profileData.createdAt)}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  </Grid>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default UserProfile;
